/**
 * Internationalization (i18n) System
 * Provides multi-language support for XM-MINOTAURO
 */

// Supported languages
export const LANGUAGES = {
  es: { code: 'es', name: 'Español', nativeName: 'Español', flag: '🇪🇸' },
  en: { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' }
};

export const DEFAULT_LANGUAGE = 'es';

// Translations
const translations = {
  es: {
    // Grafo de identidades → BAS ("Probar ruta")
    graphToBas: {
      testRoute: 'Probar ruta',
      title: 'Cobertura BAS de la ruta',
      subtitle: 'Simulación (dry-run) de cobertura por salto',
      routeCoverage: 'Cobertura de la ruta',
      coveredHop: 'cubierto ({n} atomics)',
      uncoveredHop: 'sin cobertura',
      provenHop: 'Probada',
      provenCount: '{n} probadas',
      chainCoherent: 'Cadena coherente',
      chainBroken: 'Cadena rota en el salto #{n}',
      hopMissing: 'Falta: {caps}',
      packAtomics: 'Atomics del pack sugerido',
      createPack: 'Crear pack con estas técnicas',
      close: 'Cerrar',
      noEdges: 'Esta ruta no tiene saltos para probar.',
      prefilledFromRoute: 'Técnicas sugeridas desde la ruta del grafo: {route}. Inclúyelas al crear o sembrar un pack.',
      // Verificación real de la ruta (rol ejecutor+)
      soloVer: 'Solo ver',
      verificar: 'Verificar',
      verifyRoute: 'Verificar ruta',
      verifyTitle: 'Verificar ruta (ejecución real)',
      selectAgent: 'Agente objetivo',
      loadingAgents: 'Cargando agentes…',
      noAgents: 'No hay agentes vivos en tu tenant.',
      dangerousSteps: 'Pasos peligrosos de esta ruta',
      irreversible: 'irreversible',
      intrusive: 'intrusivo',
      executeWarning: 'Ejecutar lanza atomics REALES contra equipos vivos: hace ruido en tu SIEM/EDR y puede alterar sistemas. Úsalo solo con autorización.',
      confirmExecution: 'Entiendo y autorizo la ejecución de esta ruta contra el agente seleccionado.',
      requiresConfirmation: 'La ruta tiene pasos peligrosos y requiere confirmación explícita.',
      executing: 'Ejecutando y esperando resultados…',
      result: 'Resultado de la verificación',
      executed: 'ejecutado',
      detected: 'detectado',
      executedCount: '{n} ejecutados',
      detectedCount: '{n} detectados',
      noCampaign: 'El backend no devolvió una campaña para seguir.',
    },
    // Tendencia del grafo de identidades (digital twin)
    identityTrend: {
      title: 'Tendencia del grafo de identidades',
      pathsToDA: 'Rutas a activos críticos',
      kerberoastable: 'Kerberoasteables',
      chokeTop: 'Cuellos de botella',
      loading: 'Cargando tendencia…',
      empty: 'Necesita histórico para mostrar la tendencia (activa el scheduler de diffs o guarda varios snapshots).',
    },
    // Remediación priorizada del grafo de identidades
    identityRemediation: {
      title: 'Prioriza estas correcciones',
      subtitle: 'Cada acción cierra el máximo de rutas hacia tus joyas de la corona.',
      loading: 'Calculando remediación…',
      empty: 'Sin rutas a activos críticos: nada que remediar ahora mismo.',
      closesBadge: 'cierra {n} rutas ({pct}%)',
      closesTooltip: 'Rutas a activos críticos que se cierran al aplicar esta corrección',
      aiToggle: 'Resumen IA',
      aiSummaryTitle: 'Resumen ejecutivo',
      aiBasicLabel: 'resumen básico (IA no configurada)',
      aiUnavailable: 'No hay resumen disponible.',
    },
    // Form de creación de pack ad-hoc (analyst+)
    packForm: {
      createPack: 'Crear pack',
      title: 'Crear pack de ataque',
      subtitle: 'Pack personalizado de tu tenant con los atomics seleccionados.',
      name: 'Nombre',
      namePlaceholder: 'Ej: Ransomware Q1 2026',
      slug: 'Slug (único)',
      category: 'Categoría',
      description: 'Descripción',
      descriptionPlaceholder: 'Descripción opcional del pack…',
      atomics: 'Atomic tests',
      atomicsSearch: 'Buscar por nombre o ID MITRE…',
      atomicsSelected: '{n} seleccionados',
      atomicsMax: 'máximo alcanzado',
      atomicsEmpty: 'Ningún atomic coincide con la búsqueda.',
      atomicsError: 'No se pudieron cargar los atomics',
      submit: 'Crear pack',
      creating: 'Creando…',
      created: 'Pack creado: {name}',
      createdBanner: 'Pack "{name}" creado correctamente.',
      launchCampaign: 'Lanzar campaña',
      missingAtomics: 'Atomics no encontrados en tu tenant (rechazados):',
      errNameRequired: 'El nombre es obligatorio.',
      errSlugRequired: 'El slug es obligatorio.',
      errNoAtomics: 'Selecciona al menos un atomic test.',
      errTooManyAtomics: 'Máximo {max} atomics por pack.',
      errGeneric: 'No se pudo crear el pack.',
      delete: 'Borrar',
      deleting: 'Borrando…',
      deleteTitle: 'Borrar pack',
      deleteConfirm:
        '¿Seguro que quieres borrar el pack "{name}"? Esta acción no se puede deshacer.',
      deleted: 'Pack borrado: {name}',
      deleteInUse:
        'El pack tiene campañas asociadas, no se puede borrar. Borra primero sus campañas.',
      deleteFailed: 'No se pudo borrar el pack.',
    },
    // Common
    common: {
      loading: 'Cargando...',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      create: 'Crear',
      search: 'Buscar',
      searchEllipsis: 'Buscar...',
      filter: 'Filtrar',
      export: 'Exportar',
      import: 'Importar',
      refresh: 'Actualizar',
      back: 'Volver',
      next: 'Siguiente',
      previous: 'Anterior',
      yes: 'Sí',
      no: 'No',
      confirm: 'Confirmar',
      close: 'Cerrar',
      actions: 'Acciones',
      status: 'Estado',
      name: 'Nombre',
      description: 'Descripción',
      type: 'Tipo',
      date: 'Fecha',
      time: 'Hora',
      all: 'Todos',
      none: 'Ninguno',
      unknown: 'Desconocido',
      noData: 'Sin datos',
      error: 'Error',
      success: 'Éxito',
      warning: 'Advertencia',
      info: 'Información',
      viewAll: 'Ver todo',
      new: 'NUEVO',
      download: 'Descargar',
      signOut: 'Cerrar Sesión',
      systemOnline: 'Sistema en línea',
      initializing: 'Inicializando Plataforma de Seguridad...',
      securedWith: 'Asegurado con autenticación JWT y cifrado'
    },

    // Navigation
    nav: {
      dashboard: 'Dashboard',
      overview: 'Vista General',
      operations: 'Operaciones',
      detectRespond: 'Detectar y Responder',
      system: 'Sistema',
      agents: 'Agentes',
      agentHealth: 'Salud de Agentes',
      agentGroups: 'Grupos de Agentes',
      networkMap: 'Mapa de Red',
      scanner: 'Escáner',
      vulnerabilities: 'Vulnerabilidades',
      threatIntel: 'Threat Intelligence',
      iocManager: 'Gestor IOC',
      edr: 'EDR',
      antivirus: 'Antivirus',
      dlp: 'DLP',
      adAttacks: 'Ataques AD',
      emailSecurity: 'Seguridad de Email',
      compliance: 'Compliance',
      complianceDrift: 'Drift',
      reports: 'Reportes',
      scheduledReports: 'Programados',
      siemExport: 'Exportar SIEM',
      alerts: 'Alertas',
      remediation: 'Remediación',
      dataLeaks: 'Fugas de Datos',
      metrics: 'Métricas',
      miniSiem: 'Mini SIEM',
      settings: 'Configuración',
      users: 'Usuarios',
      infrastructure: 'Infraestructura',
      sso: 'SSO/SAML',
      license: 'Licencia',
      connectors: 'Conectores',
      ipFilter: 'Filtro IP',
      dataRetention: 'Retención',
      auditTrail: 'Auditoría',
      apiDocs: 'Especificación API (OpenAPI)',
      integrations: 'Integraciones',
      networkGraph: 'Topología de Red',
      logout: 'Cerrar Sesión',
      admin: 'Admin',
      response: 'Respuesta',
      monitoring: 'Monitoreo',
      security: 'Seguridad',
      guidedTour: 'Tour Guiado',
      startGuidedTour: 'Iniciar tour guiado',
      docCenter: 'Documentación',
      threatMap: 'Mapa de Amenazas',
      ingestionPipeline: 'Pipeline de Ingesta',
      certificates: 'Certificados',
      attackLibrary: 'Biblioteca de Ataques',
      runHistory: 'Historial de Ejecución',
      attackPacks: 'Packs de Ataques',
      attackPaths: 'Rutas de Ataque',
      aiMutation: 'Mutación IA',
      detection: 'Detección',
      sigmaRules: 'Reglas Sigma',
      yaraRules: 'Reglas YARA',
      correlation: 'Correlación',
      mitreCoverage: 'Cobertura MITRE',
      basValidation: 'Validación BAS',
      whatsNew: 'Novedades',
      securityRoadmap: 'Tu seguridad',
      threatModel: 'Modelado de amenazas',
      ctiInbox: 'CTI Auto-Ingest',
      externalSiem: 'External SIEM'
    },

    // Dashboard
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Estado global del entorno monitorizado',
      updated: 'Actualizado',
      noData: 'sin datos',
      securityDashboard: 'Panel de Seguridad',
      securityScore: 'Puntuación de Seguridad',
      securityScoreTooltip: 'Puntuación de seguridad global (0-100)',
      activeAgents: 'Agentes Activos',
      activeAgentsTooltip: 'Agentes de monitoreo activos en la red',
      allSystemsOperational: 'Todos los sistemas operativos',
      criticalVulnerabilities: 'Vulnerabilidades Críticas',
      criticalVulnsTooltip: 'CVEs críticas que requieren atención inmediata',
      needAttention: 'requieren atención',
      attackPaths: 'Rutas de Ataque',
      attackPathsTooltip: 'Rutas de ataque potenciales hacia activos críticos',
      identified: 'identificadas',
      toCriticalAssets: 'Hacia activos críticos',
      openVulnerabilities: 'Vulnerabilidades Abiertas',
      pendingAlerts: 'Alertas Pendientes',
      recentActivity: 'Actividad Reciente',
      topVulnerabilities: 'Top 5 Vulnerabilidades Críticas',
      topVulnsSubtitle: 'Problemas de mayor prioridad que requieren atención inmediata',
      complianceStatus: 'Estado de Compliance',
      riskDistribution: 'Distribución de Riesgo',
      criticalFindings: 'Hallazgos Críticos',
      recommendations: 'Recomendaciones',
      whatToDoNow: '¿Qué hacer ahora?',
      securityTrend: 'Tendencia de Seguridad',
      scoreProgression: 'Progresión del score en el tiempo',
      riskEvolution: 'Evolución del Riesgo',
      vulnTrendsWeeks: 'Tendencia de vulnerabilidades en las últimas 4 semanas',
      collectingData: 'Recopilando datos de riesgo...',
      trendsAfterWeek: 'Las tendencias aparecerán después de la primera semana de monitoreo',
      highRiskScenarios: 'Escenarios de Alto Riesgo',
      highRiskSubtitle: 'Vulnerabilidades críticas que requieren acción inmediata',
      noCriticalVulns: 'No se detectaron vulnerabilidades críticas',
      criticalVulnDetected: 'Vulnerabilidad crítica detectada',
      vulnDistribution: 'Distribución de Vulnerabilidades',
      topAffectedAssets: 'Activos Más Afectados',
      quickActions: 'Acciones Rápidas',
      runAttackSim: 'Ejecutar Simulación de Ataque',
      runAttackSimTooltip: 'Simula ataques de ransomware, phishing y más',
      generateReport: 'Generar Reporte',
      generateReportTooltip: 'Genera reportes ejecutivos y técnicos',
      viewNetworkMap: 'Ver Mapa de Red',
      viewNetworkMapTooltip: 'Visualiza la topología de tu red',
      remediateNow: 'Remediar Ahora',
      remediate: 'Remediar',
      viewCVE: 'Ver CVE',
      totalCritical: 'Total Críticas',
      highPriority: 'Alta Prioridad',
      affectedHosts: 'Hosts Afectados',
      vsLastWeek: 'vs semana pasada',
      fromLastScan: 'desde último escaneo',
      updatedMinAgo: 'Actualizado hace {{minutes}} min',
      assetsAtRisk: 'Activos en riesgo',
      active: 'activos',
      noAssessments: 'Sin evaluaciones',
      noIncidents: 'Sin incidentes',
      resolvedDays: '{{count}} resueltos (30d)',
      avgCriticalUnpatched: 'Promedio sin parchar (críticas)',
      meanTimeToDetect: 'Tiempo Medio de Detección',
      meanTimeToRespond: 'Tiempo Medio de Respuesta',
      daysAgo: 'd atrás',
      frameworks: 'frameworks'
    },

    // Agents
    agents: {
      title: 'Agentes',
      addAgent: 'Añadir Agente',
      hostname: 'Hostname',
      ipAddress: 'Dirección IP',
      os: 'Sistema Operativo',
      version: 'Versión',
      lastSeen: 'Última Conexión',
      status: 'Estado',
      active: 'Activo',
      inactive: 'Inactivo',
      error: 'Error',
      installGuide: 'Guía de Instalación',
      installCommand: 'Comando de Instalación',
      copyCommand: 'Copiar Comando',
      groups: 'Grupos',
      configuration: 'Configuración',
      noAgents: 'No hay agentes registrados'
    },

    // Vulnerabilities
    vulnerabilities: {
      title: 'Vulnerabilidades',
      severity: 'Severidad',
      critical: 'Crítica',
      high: 'Alta',
      medium: 'Media',
      low: 'Baja',
      info: 'Informativa',
      affectedAssets: 'Activos Afectados',
      remediation: 'Remediación',
      cve: 'CVE',
      cvss: 'CVSS',
      discovered: 'Descubierta',
      status: 'Estado',
      open: 'Abierta',
      inProgress: 'En Progreso',
      resolved: 'Resuelta',
      falsePositive: 'Falso Positivo'
    },

    // Compliance
    compliance: {
      title: 'Compliance',
      frameworks: 'Marcos de Referencia',
      controls: 'Controles',
      compliant: 'Conforme',
      nonCompliant: 'No Conforme',
      partial: 'Parcial',
      notApplicable: 'No Aplica',
      evidence: 'Evidencia',
      generateReport: 'Generar Reporte',
      lastAssessment: 'Última Evaluación',
      overallScore: 'Puntuación General',
      drift: 'Cambios Detectados'
    },

    // Alerts
    alerts: {
      title: 'Alertas',
      newAlert: 'Nueva Alerta',
      acknowledge: 'Reconocer',
      resolve: 'Resolver',
      escalate: 'Escalar',
      assignTo: 'Asignar a',
      priority: 'Prioridad',
      source: 'Origen',
      timestamp: 'Fecha/Hora',
      details: 'Detalles'
    },

    // Reports
    reports: {
      title: 'Reportes',
      generate: 'Generar Reporte',
      schedule: 'Programar',
      export: 'Exportar',
      pdf: 'PDF',
      csv: 'CSV',
      excel: 'Excel',
      executiveSummary: 'Resumen Ejecutivo',
      technicalReport: 'Reporte Técnico',
      complianceReport: 'Reporte de Compliance'
    },

    // Settings
    settings: {
      title: 'Configuración',
      general: 'General',
      security: 'Seguridad',
      notifications: 'Notificaciones',
      integrations: 'Integraciones',
      users: 'Usuarios',
      language: 'Idioma',
      theme: 'Tema',
      timezone: 'Zona Horaria',
      dataRetention: 'Retención de Datos'
    },

    // Authentication
    auth: {
      // Cambio de contraseña (primera pantalla de todo usuario nuevo).
      security: 'Seguridad',
      changePasswordTitle: 'Cambia tu contraseña',
      changePasswordRequired: 'Debes establecer una contraseña nueva antes de continuar.',
      changePasswordSubtitle: 'Actualiza la contraseña de tu cuenta.',
      currentPassword: 'Contraseña actual',
      newPassword: 'Contraseña nueva',
      confirmPassword: 'Confirma la contraseña nueva',
      updatePassword: 'Actualizar contraseña',
      saving: 'Guardando...',
      passwordTooShort: 'La contraseña debe tener al menos 8 caracteres',
      passwordsDoNotMatch: 'Las contraseñas no coinciden',
      passwordMustDiffer: 'La contraseña nueva debe ser distinta de la actual',
      passwordChangeFailed: 'No se pudo cambiar la contraseña',
      login: 'Iniciar Sesión',
      logout: 'Cerrar Sesión',
      email: 'Correo Electrónico',
      emailAddress: 'Dirección de Email',
      password: 'Contraseña',
      rememberMe: 'Recordarme',
      forgotPassword: '¿Olvidaste tu contraseña?',
      resetPassword: 'Restablecer Contraseña',
      mfa: 'Autenticación de Dos Factores',
      mfaTitle: 'Autenticación de Dos Factores',
      mfaSubtitle: 'Introduce el código de 6 dígitos de tu app de autenticación',
      verificationCode: 'Código de Verificación',
      openAuthApp: 'Abre Google Authenticator, Authy o similar',
      verifying: 'Verificando...',
      verifyAndSignIn: 'Verificar e Iniciar Sesión',
      useDifferentAccount: 'Usar una cuenta diferente',
      sso: 'Inicio de Sesión Único',
      loginWith: 'Iniciar sesión con',
      welcomeBack: 'Bienvenido de nuevo',
      signInSubtitle: 'Inicia sesión para acceder a tu panel de seguridad',
      signIn: 'Iniciar Sesión',
      signingIn: 'Autenticando...',
      enterPassword: 'Introduce tu contraseña',
      invalidCredentials: 'Credenciales incorrectas',
      invalidMfaCode: 'Código MFA incorrecto',
      enterpriseGrade: 'Nivel Empresarial',
      loginHeroDesc: 'Plataforma de validación continua de seguridad que identifica rutas de ataque y vulnerabilidades antes que los adversarios.',
      attackPathsFeature: 'Rutas de Ataque',
      killChainAnalysis: 'Análisis kill chain',
      adSecurity: 'Seguridad AD',
      bloodhoundStyle: 'Estilo BloodHound',
      realTime: 'Tiempo Real',
      liveMonitoring: 'Monitoreo en vivo',
      zeroDayArena: 'Arena 0-Day',
      securityPlatform: 'Plataforma de Seguridad',
      enterpriseSecurity: 'Seguridad Empresarial',
      ssoWithSaml: 'SSO con SAML',
      footer: {
        aes256: 'AES-256',
        soc2: 'SOC 2',
        gdpr: 'GDPR'
      }
    },

    // License
    license: {
      title: 'Licencia',
      currentPlan: 'Plan Actual',
      upgrade: 'Mejorar Plan',
      free: 'Gratuito',
      pro: 'Profesional',
      enterprise: 'Empresarial',
      features: 'Características',
      limits: 'Límites',
      usage: 'Uso',
      activateLicense: 'Activar Licencia',
      licenseKey: 'Clave de Licencia'
    },

    // Network Graph
    networkGraph: {
      title: 'Topología de Red',
      nodes: 'Nodos',
      connections: 'Conexiones',
      server: 'Servidor',
      port: 'Puerto',
      service: 'Servicio',
      user: 'Usuario',
      discoveredHost: 'Host Descubierto',
      riskLevel: 'Nivel de Riesgo',
      details: 'Detalles',
      legend: 'Leyenda',
      zoomIn: 'Acercar',
      zoomOut: 'Alejar',
      center: 'Centrar',
      reset: 'Reiniciar'
    },

    // Remediation
    remediation: {
      title: 'Remediación',
      pending: 'Pendientes',
      approved: 'Aprobadas',
      rejected: 'Rechazadas',
      executing: 'Ejecutando',
      completed: 'Completadas',
      approve: 'Aprobar',
      reject: 'Rechazar',
      rollback: 'Revertir',
      script: 'Script',
      history: 'Historial'
    },

    // Documentation Center
    docs: {
      title: 'Centro de Documentación',
      subtitle: 'Todo lo que necesitas para gestionar tu plataforma de seguridad',
      searchPlaceholder: 'Buscar en la documentación...',
      totalArticles: 'artículos',
      resultsFor: 'resultados para',
      clearSearch: 'Limpiar búsqueda',
      noResults: 'Sin resultados',
      noResultsDesc: 'No se encontraron artículos que coincidan con tu búsqueda.',
      quickLinks: 'Accesos Rápidos',
      popularTopics: 'Temas Populares',
      needHelp: '¿Necesitas ayuda?',
      needHelpDesc: 'No encuentras lo que buscas? Contacta con nuestro equipo de soporte.',
      contactSupport: 'Contactar Soporte',
      githubRepo: 'Repositorio GitHub',
      apiPlayground: 'API Playground',
      changelog: 'Changelog',
      statusPage: 'Estado del Sistema',
      releaseNotes: 'Notas de Versión',
      // Categories
      gettingStarted: 'Primeros Pasos',
      gettingStartedDesc: 'Guías de inicio rápido, instalación y configuración inicial de la plataforma.',
      agentDeployment: 'Despliegue de Agentes',
      agentDeploymentDesc: 'Instalación y gestión de agentes en Windows, Linux y mediante políticas de grupo.',
      securityModules: 'Módulos de Seguridad',
      securityModulesDesc: 'Email Security, gestión IOC, SIEM, escáner de vulnerabilidades, ataques AD y compliance.',
      apiReference: 'Referencia API',
      apiReferenceDesc: 'Documentación de la API REST, autenticación, webhooks y límites de uso.',
      administration: 'Administración',
      administrationDesc: 'Gestión de usuarios, RBAC, configuración SSO y multi-tenancy.',
      troubleshooting: 'Resolución de Problemas',
      troubleshootingDesc: 'Problemas comunes, conectividad de agentes, fallos de escaneo y análisis de logs.',
      // Articles - Getting Started
      quickStartGuide: 'Guía de Inicio Rápido',
      quickStartGuideDesc: 'Pon en marcha la plataforma en menos de 15 minutos con esta guía paso a paso.',
      installationGuide: 'Guía de Instalación',
      installationGuideDesc: 'Requisitos del sistema, instalación de Docker y configuración del entorno.',
      firstScan: 'Tu Primer Escaneo',
      firstScanDesc: 'Ejecuta tu primer escaneo de vulnerabilidades y comprende los resultados.',
      architectureOverview: 'Visión General de la Arquitectura',
      architectureOverviewDesc: 'Componentes del sistema, flujos de datos y diseño de seguridad.',
      // Articles - Agent Deployment
      windowsAgent: 'Agente Windows',
      windowsAgentDesc: 'Instalación y configuración del agente en entornos Windows Server y Desktop.',
      linuxAgent: 'Agente Linux',
      linuxAgentDesc: 'Despliegue del agente en distribuciones Debian, Ubuntu, RHEL y CentOS.',
      groupPolicies: 'Despliegue por GPO',
      groupPoliciesDesc: 'Despliegue masivo de agentes mediante Group Policy Objects en Active Directory.',
      autoUpdate: 'Actualización Automática',
      autoUpdateDesc: 'Configuración del mecanismo de auto-actualización de agentes.',
      agentTroubleshooting: 'Diagnóstico de Agentes',
      agentTroubleshootingDesc: 'Solución de problemas comunes de conexión y rendimiento de agentes.',
      // Articles - Security Modules
      emailSecurityDoc: 'Seguridad de Email',
      emailSecurityDocDesc: 'Análisis SPF, DKIM, DMARC y detección de amenazas en correo electrónico.',
      iocManagement: 'Gestión de IOC',
      iocManagementDesc: 'Importación, búsqueda y gestión de indicadores de compromiso.',
      miniSiemDoc: 'Mini SIEM',
      miniSiemDocDesc: 'Recolección y análisis de eventos de seguridad en tiempo real.',
      vulnScanner: 'Escáner de Vulnerabilidades',
      vulnScannerDesc: 'Escaneo de vulnerabilidades CVE y evaluación de riesgo por activo.',
      adAttacksDoc: 'Ataques Active Directory',
      adAttacksDocDesc: 'Detección de rutas de ataque estilo BloodHound en entornos AD.',
      complianceModule: 'Módulo de Compliance',
      complianceModuleDesc: 'Evaluación de cumplimiento GDPR, ISO 27001, NIST, PCI-DSS y ENS.',
      // Articles - API Reference
      restApiDocs: 'Documentación API REST',
      restApiDocsDesc: 'Referencia completa de endpoints, parámetros y respuestas de la API.',
      authenticationApi: 'Autenticación API',
      authenticationApiDesc: 'JWT tokens, API keys y flujos de autenticación para integraciones.',
      webhooks: 'Webhooks',
      webhooksDesc: 'Configuración de webhooks para recibir notificaciones en tiempo real.',
      rateLimits: 'Límites de Uso',
      rateLimitsDesc: 'Rate limiting, cuotas y mejores prácticas para consumo de API.',
      // Articles - Administration
      userManagement: 'Gestión de Usuarios',
      userManagementDesc: 'Creación, edición y gestión de cuentas de usuario y permisos.',
      rbac: 'Control de Acceso (RBAC)',
      rbacDesc: 'Configuración de roles, permisos y políticas de acceso basadas en roles.',
      ssoConfig: 'Configuración SSO',
      ssoConfigDesc: 'Integración con SAML, OAuth2 y proveedores de identidad corporativos.',
      multiTenancy: 'Multi-Tenancy',
      multiTenancyDesc: 'Configuración y gestión de múltiples tenants y aislamiento de datos.',
      // Articles - Troubleshooting
      commonIssues: 'Problemas Comunes',
      commonIssuesDesc: 'Soluciones a los problemas más frecuentes reportados por usuarios.',
      agentConnectivity: 'Conectividad de Agentes',
      agentConnectivityDesc: 'Diagnóstico de problemas de red, firewall y conectividad con el backend.',
      scanFailures: 'Fallos de Escaneo',
      scanFailuresDesc: 'Resolución de escaneos fallidos, timeouts y errores de permisos.',
      logAnalysis: 'Análisis de Logs',
      logAnalysisDesc: 'Ubicación, interpretación y uso de logs para diagnóstico avanzado.',
    },

    // AD Attacks
    adAttacks: {
      scanFailedTag: 'Escaneo AD fallido',
      scanFailedTitle: 'El escaneo AD se ejecutó pero falló.',
      scanFailedDetails: 'Errores por sección:',
      scanFailedHint: 'Revisa las credenciales, la conectividad con el Domain Controller y que el agente esté unido al dominio; después vuelve a lanzar el escaneo.',
    },

    // Errors
    errors: {
      generic: 'Ha ocurrido un error',
      network: 'Error de conexión',
      unauthorized: 'No autorizado',
      forbidden: 'Acceso denegado',
      notFound: 'No encontrado',
      serverError: 'Error del servidor',
      validation: 'Error de validación',
      sessionExpired: 'Sesión expirada'
    },

    // Success messages
    success: {
      saved: 'Guardado correctamente',
      deleted: 'Eliminado correctamente',
      updated: 'Actualizado correctamente',
      created: 'Creado correctamente',
      imported: 'Importado correctamente',
      exported: 'Exportado correctamente'
    },

    // Aviso «no disponible» por bloque: el backend marca en `_degraded` qué
    // trozo de la respuesta no pudo calcular. Antes se pintaba un cero y el
    // usuario lo tomaba por dato. Ver lib/degraded.js.
    degraded: {
      notice: 'Dato no disponible ahora mismo: {blocks}. No se ha podido leer, no es un cero real.',
      chip: 'no disponible',
      blocks: {
        response: 'estos datos',
        today_score: 'la nota de hoy',
        mttd: 'tiempo medio de detección (MTTD)',
        mttr: 'tiempo medio de resolución (MTTR)',
        compliance: 'cumplimiento',
        exposure: 'exposición',
        incidents: 'incidentes',
        attack_surface: 'superficie de ataque',
        dlp: 'fuga de datos (DLP)',
        patch_lag: 'retraso de parches',
        agents: 'agentes',
        scans: 'escaneos',
        vulnerabilities: 'vulnerabilidades',
        ad_scans: 'escaneos de Active Directory',
        network_scans: 'escaneos de red',
        attack_paths: 'rutas de ataque',
        database: 'base de datos',
        alerts: 'alertas',
        ingestion: 'ingesta de eventos',
        trends: 'tendencias del panel',
        executive: 'resumen ejecutivo',
        packs: 'packs de ataque',
        activations: 'activaciones de packs',
        correlation_rules: 'reglas de correlación',
        correlation_alerts: 'alertas de correlación',
        correlation_stats: 'estadísticas de correlación',
        bas_dashboard: 'resumen BAS',
        bas_trend: 'evolución de la nota BAS',
        bas_campaigns: 'campañas BAS',
        bas_vendors: 'catálogo de fabricantes',
        bas_siem_vendors: 'stack SIEM/EDR guardado',
        attack_library_tests: 'pruebas de la biblioteca de ataques',
        siem_config: 'configuración del SIEM (se muestran los valores por defecto)',
        agent_version: 'última versión del agente',
        runbook: 'el runbook sugerido para esta alerta'
      }
    }
  },

  en: {
    // Identity graph → BAS ("Test route")
    graphToBas: {
      testRoute: 'Test route',
      title: 'Route BAS coverage',
      subtitle: 'Dry-run coverage simulation per hop',
      routeCoverage: 'Route coverage',
      coveredHop: 'covered ({n} atomics)',
      uncoveredHop: 'no coverage',
      provenHop: 'Proven',
      provenCount: '{n} proven',
      chainCoherent: 'Coherent chain',
      chainBroken: 'Chain broken at hop #{n}',
      hopMissing: 'Missing: {caps}',
      packAtomics: 'Suggested pack atomics',
      createPack: 'Create pack with these techniques',
      close: 'Close',
      noEdges: 'This route has no hops to test.',
      prefilledFromRoute: 'Techniques suggested from the graph route: {route}. Include them when creating or seeding a pack.',
      // Real route verification (executor role and above)
      soloVer: 'View only',
      verificar: 'Verify',
      verifyRoute: 'Verify route',
      verifyTitle: 'Verify route (real execution)',
      selectAgent: 'Target agent',
      loadingAgents: 'Loading agents…',
      noAgents: 'No live agents in your tenant.',
      dangerousSteps: 'Dangerous steps in this route',
      irreversible: 'irreversible',
      intrusive: 'intrusive',
      executeWarning: 'Running launches REAL atomics against live hosts: it makes noise in your SIEM/EDR and may alter systems. Use only with authorization.',
      confirmExecution: 'I understand and authorize executing this route against the selected agent.',
      requiresConfirmation: 'The route has dangerous steps and requires explicit confirmation.',
      executing: 'Running and waiting for results…',
      result: 'Verification result',
      executed: 'executed',
      detected: 'detected',
      executedCount: '{n} executed',
      detectedCount: '{n} detected',
      noCampaign: 'The backend did not return a campaign to track.',
    },
    // Identity graph trend (digital twin)
    identityTrend: {
      title: 'Identity graph trend',
      pathsToDA: 'Paths to crown jewels',
      kerberoastable: 'Kerberoastable',
      chokeTop: 'Choke points',
      loading: 'Loading trend…',
      empty: 'History required to show the trend (enable the diff scheduler or save several snapshots).',
    },
    // Prioritized identity graph remediation
    identityRemediation: {
      title: 'Fix these first',
      subtitle: 'Each action closes the most paths to your crown jewels.',
      loading: 'Computing remediation…',
      empty: 'No paths to crown jewels: nothing to remediate right now.',
      closesBadge: 'closes {n} paths ({pct}%)',
      closesTooltip: 'Paths to crown jewels closed by applying this fix',
      aiToggle: 'AI summary',
      aiSummaryTitle: 'Executive summary',
      aiBasicLabel: 'basic summary (AI not configured)',
      aiUnavailable: 'No summary available.',
    },
    // Ad-hoc pack creation form (analyst+)
    packForm: {
      createPack: 'Create pack',
      title: 'Create attack pack',
      subtitle: 'Tenant-scoped custom pack with the selected atomics.',
      name: 'Name',
      namePlaceholder: 'e.g. Ransomware Q1 2026',
      slug: 'Slug (unique)',
      category: 'Category',
      description: 'Description',
      descriptionPlaceholder: 'Optional pack description…',
      atomics: 'Atomic tests',
      atomicsSearch: 'Search by name or MITRE id…',
      atomicsSelected: '{n} selected',
      atomicsMax: 'max reached',
      atomicsEmpty: 'No atomics match your search.',
      atomicsError: 'Failed to load atomics',
      submit: 'Create pack',
      creating: 'Creating…',
      created: 'Pack created: {name}',
      createdBanner: 'Pack "{name}" created successfully.',
      launchCampaign: 'Launch campaign',
      missingAtomics: 'Atomics not found in your tenant (rejected):',
      errNameRequired: 'Name is required.',
      errSlugRequired: 'Slug is required.',
      errNoAtomics: 'Select at least one atomic test.',
      errTooManyAtomics: 'Max {max} atomics per pack.',
      errGeneric: 'Could not create the pack.',
      delete: 'Delete',
      deleting: 'Deleting…',
      deleteTitle: 'Delete pack',
      deleteConfirm:
        'Are you sure you want to delete the pack "{name}"? This action cannot be undone.',
      deleted: 'Pack deleted: {name}',
      deleteInUse:
        'The pack has associated campaigns and cannot be deleted. Delete its campaigns first.',
      deleteFailed: 'Could not delete the pack.',
    },
    // Common
    common: {
      loading: 'Loading...',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      create: 'Create',
      search: 'Search',
      searchEllipsis: 'Search...',
      filter: 'Filter',
      export: 'Export',
      import: 'Import',
      refresh: 'Refresh',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      yes: 'Yes',
      no: 'No',
      confirm: 'Confirm',
      close: 'Close',
      actions: 'Actions',
      status: 'Status',
      name: 'Name',
      description: 'Description',
      type: 'Type',
      date: 'Date',
      time: 'Time',
      all: 'All',
      none: 'None',
      unknown: 'Unknown',
      noData: 'No data',
      error: 'Error',
      success: 'Success',
      warning: 'Warning',
      info: 'Information',
      viewAll: 'View all',
      new: 'NEW',
      download: 'Download',
      signOut: 'Sign Out',
      systemOnline: 'System Online',
      initializing: 'Initializing Security Platform...',
      securedWith: 'Secured with JWT authentication & encryption'
    },

    // Navigation
    nav: {
      dashboard: 'Dashboard',
      overview: 'Overview',
      operations: 'Operations',
      detectRespond: 'Detect & Respond',
      system: 'System',
      agents: 'Agents',
      agentHealth: 'Agent Health',
      agentGroups: 'Agent Groups',
      networkMap: 'Network Map',
      scanner: 'Scanner',
      vulnerabilities: 'Vulnerabilities',
      threatIntel: 'Threat Intel',
      iocManager: 'IOC Manager',
      edr: 'EDR',
      antivirus: 'Antivirus',
      dlp: 'DLP',
      adAttacks: 'AD Attacks',
      emailSecurity: 'Email Security',
      compliance: 'Compliance',
      complianceDrift: 'Drift',
      reports: 'Reports',
      scheduledReports: 'Scheduled',
      siemExport: 'SIEM Export',
      alerts: 'Alerts',
      remediation: 'Remediation',
      dataLeaks: 'Data Leaks',
      metrics: 'Metrics',
      miniSiem: 'Mini SIEM',
      settings: 'Settings',
      users: 'Users',
      infrastructure: 'Infrastructure',
      sso: 'SSO/SAML',
      license: 'License',
      connectors: 'Connectors',
      ipFilter: 'IP Filter',
      dataRetention: 'Retention',
      auditTrail: 'Audit Trail',
      apiDocs: 'API Specification (OpenAPI)',
      integrations: 'Integrations',
      networkGraph: 'Network Topology',
      logout: 'Logout',
      admin: 'Admin',
      response: 'Response',
      monitoring: 'Monitoring',
      security: 'Security',
      guidedTour: 'Guided Tour',
      startGuidedTour: 'Start guided tour',
      docCenter: 'Documentation',
      threatMap: 'Threat Map',
      ingestionPipeline: 'Ingestion Pipeline',
      certificates: 'Certificates',
      attackLibrary: 'Attack Library',
      runHistory: 'Run History',
      attackPacks: 'Attack Packs',
      attackPaths: 'Attack Paths',
      aiMutation: 'AI Mutation',
      detection: 'Detection',
      sigmaRules: 'Sigma Rules',
      yaraRules: 'YARA Rules',
      correlation: 'Correlation',
      mitreCoverage: 'MITRE Coverage',
      basValidation: 'BAS Validation',
      whatsNew: "What's New",
      securityRoadmap: 'Your security',
      threatModel: 'Threat modeling',
      ctiInbox: 'CTI Auto-Ingest',
      externalSiem: 'External SIEM'
    },

    // Dashboard
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Global state of monitored environment',
      updated: 'Updated',
      noData: 'no data',
      securityDashboard: 'Security Dashboard',
      securityScore: 'Security Score',
      securityScoreTooltip: 'Overall security score (0-100)',
      activeAgents: 'Active Agents',
      activeAgentsTooltip: 'Active monitoring agents on the network',
      allSystemsOperational: 'All systems operational',
      criticalVulnerabilities: 'Critical Vulnerabilities',
      criticalVulnsTooltip: 'Critical CVEs requiring immediate attention',
      needAttention: 'need attention',
      attackPaths: 'Attack Paths',
      attackPathsTooltip: 'Potential attack paths to critical assets',
      identified: 'identified',
      toCriticalAssets: 'To critical assets',
      openVulnerabilities: 'Open Vulnerabilities',
      pendingAlerts: 'Pending Alerts',
      recentActivity: 'Recent Activity',
      topVulnerabilities: 'Top 5 Critical Vulnerabilities',
      topVulnsSubtitle: 'Highest priority issues requiring immediate attention',
      complianceStatus: 'Compliance Status',
      riskDistribution: 'Risk Distribution',
      criticalFindings: 'Critical Findings',
      recommendations: 'Recommendations',
      whatToDoNow: 'What to do now?',
      securityTrend: 'Security Trend',
      scoreProgression: 'Score progression over time',
      riskEvolution: 'Risk Evolution',
      vulnTrendsWeeks: 'Vulnerability trends over the last 4 weeks',
      collectingData: 'Collecting risk data...',
      trendsAfterWeek: 'Trends will appear after the first week of monitoring',
      highRiskScenarios: 'High Risk Scenarios',
      highRiskSubtitle: 'Critical vulnerabilities requiring immediate action',
      noCriticalVulns: 'No critical vulnerabilities detected',
      criticalVulnDetected: 'Critical vulnerability detected',
      vulnDistribution: 'Vulnerability Distribution',
      topAffectedAssets: 'Top Affected Assets',
      quickActions: 'Quick Actions',
      runAttackSim: 'Run Attack Simulation',
      runAttackSimTooltip: 'Simulate ransomware, phishing and more attacks',
      generateReport: 'Generate Report',
      generateReportTooltip: 'Generate executive and technical reports',
      viewNetworkMap: 'View Network Map',
      viewNetworkMapTooltip: 'Visualize your network topology',
      remediateNow: 'Remediate Now',
      remediate: 'Remediate',
      viewCVE: 'View CVE',
      totalCritical: 'Total Critical',
      highPriority: 'High Priority',
      affectedHosts: 'Affected Hosts',
      vsLastWeek: 'vs last week',
      fromLastScan: 'from last scan',
      updatedMinAgo: 'Updated {{minutes}} min ago',
      assetsAtRisk: 'Assets at risk',
      active: 'active',
      noAssessments: 'No assessments',
      noIncidents: 'No incidents',
      resolvedDays: '{{count}} resolved (30d)',
      avgCriticalUnpatched: 'Avg critical unpatched',
      meanTimeToDetect: 'Mean Time to Detect',
      meanTimeToRespond: 'Mean Time to Respond',
      daysAgo: 'd ago',
      frameworks: 'frameworks'
    },

    // Agents
    agents: {
      title: 'Agents',
      addAgent: 'Add Agent',
      hostname: 'Hostname',
      ipAddress: 'IP Address',
      os: 'Operating System',
      version: 'Version',
      lastSeen: 'Last Seen',
      status: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      error: 'Error',
      installGuide: 'Installation Guide',
      installCommand: 'Installation Command',
      copyCommand: 'Copy Command',
      groups: 'Groups',
      configuration: 'Configuration',
      noAgents: 'No agents registered'
    },

    // Vulnerabilities
    vulnerabilities: {
      title: 'Vulnerabilities',
      severity: 'Severity',
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      info: 'Info',
      affectedAssets: 'Affected Assets',
      remediation: 'Remediation',
      cve: 'CVE',
      cvss: 'CVSS',
      discovered: 'Discovered',
      status: 'Status',
      open: 'Open',
      inProgress: 'In Progress',
      resolved: 'Resolved',
      falsePositive: 'False Positive'
    },

    // Compliance
    compliance: {
      title: 'Compliance',
      frameworks: 'Frameworks',
      controls: 'Controls',
      compliant: 'Compliant',
      nonCompliant: 'Non-Compliant',
      partial: 'Partial',
      notApplicable: 'Not Applicable',
      evidence: 'Evidence',
      generateReport: 'Generate Report',
      lastAssessment: 'Last Assessment',
      overallScore: 'Overall Score',
      drift: 'Changes Detected'
    },

    // Alerts
    alerts: {
      title: 'Alerts',
      newAlert: 'New Alert',
      acknowledge: 'Acknowledge',
      resolve: 'Resolve',
      escalate: 'Escalate',
      assignTo: 'Assign to',
      priority: 'Priority',
      source: 'Source',
      timestamp: 'Timestamp',
      details: 'Details'
    },

    // Reports
    reports: {
      title: 'Reports',
      generate: 'Generate Report',
      schedule: 'Schedule',
      export: 'Export',
      pdf: 'PDF',
      csv: 'CSV',
      excel: 'Excel',
      executiveSummary: 'Executive Summary',
      technicalReport: 'Technical Report',
      complianceReport: 'Compliance Report'
    },

    // Settings
    settings: {
      title: 'Settings',
      general: 'General',
      security: 'Security',
      notifications: 'Notifications',
      integrations: 'Integrations',
      users: 'Users',
      language: 'Language',
      theme: 'Theme',
      timezone: 'Timezone',
      dataRetention: 'Data Retention'
    },

    // Authentication
    auth: {
      // Forced password change (first screen every new user sees).
      security: 'Security',
      changePasswordTitle: 'Change your password',
      changePasswordRequired: 'You must set a new password before continuing.',
      changePasswordSubtitle: 'Update your account password.',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      updatePassword: 'Update password',
      saving: 'Saving...',
      passwordTooShort: 'Password must be at least 8 characters',
      passwordsDoNotMatch: 'Passwords do not match',
      passwordMustDiffer: 'New password must be different from current',
      passwordChangeFailed: 'Failed to change password',
      login: 'Login',
      logout: 'Logout',
      email: 'Email',
      emailAddress: 'Email Address',
      password: 'Password',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot password?',
      resetPassword: 'Reset Password',
      mfa: 'Two-Factor Authentication',
      mfaTitle: 'Two-Factor Authentication',
      mfaSubtitle: 'Enter the 6-digit code from your authenticator app',
      verificationCode: 'Verification Code',
      openAuthApp: 'Open Google Authenticator, Authy, or similar app',
      verifying: 'Verifying...',
      verifyAndSignIn: 'Verify & Sign In',
      useDifferentAccount: 'Use a different account',
      sso: 'Single Sign-On',
      loginWith: 'Login with',
      welcomeBack: 'Welcome back',
      signInSubtitle: 'Sign in to access your security dashboard',
      signIn: 'Sign In',
      signingIn: 'Authenticating...',
      enterPassword: 'Enter your password',
      invalidCredentials: 'Invalid credentials',
      invalidMfaCode: 'Invalid MFA code',
      enterpriseGrade: 'Enterprise-Grade',
      loginHeroDesc: 'Continuous security validation platform that identifies attack paths and vulnerabilities before adversaries do.',
      attackPathsFeature: 'Attack Paths',
      killChainAnalysis: 'Kill chain analysis',
      adSecurity: 'AD Security',
      bloodhoundStyle: 'BloodHound-style',
      realTime: 'Real-time',
      liveMonitoring: 'Live monitoring',
      zeroDayArena: '0-Day Arena',
      securityPlatform: 'Security Platform',
      enterpriseSecurity: 'Enterprise Security',
      ssoWithSaml: 'SSO with SAML',
      footer: {
        aes256: 'AES-256',
        soc2: 'SOC 2',
        gdpr: 'GDPR'
      }
    },

    // License
    license: {
      title: 'License',
      currentPlan: 'Current Plan',
      upgrade: 'Upgrade Plan',
      free: 'Free',
      pro: 'Professional',
      enterprise: 'Enterprise',
      features: 'Features',
      limits: 'Limits',
      usage: 'Usage',
      activateLicense: 'Activate License',
      licenseKey: 'License Key'
    },

    // Network Graph
    networkGraph: {
      title: 'Network Topology',
      nodes: 'Nodes',
      connections: 'Connections',
      server: 'Server',
      port: 'Port',
      service: 'Service',
      user: 'User',
      discoveredHost: 'Discovered Host',
      riskLevel: 'Risk Level',
      details: 'Details',
      legend: 'Legend',
      zoomIn: 'Zoom In',
      zoomOut: 'Zoom Out',
      center: 'Center',
      reset: 'Reset'
    },

    // Remediation
    remediation: {
      title: 'Remediation',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      executing: 'Executing',
      completed: 'Completed',
      approve: 'Approve',
      reject: 'Reject',
      rollback: 'Rollback',
      script: 'Script',
      history: 'History'
    },

    // Documentation Center
    docs: {
      title: 'Documentation Center',
      subtitle: 'Everything you need to manage your security platform',
      searchPlaceholder: 'Search documentation...',
      totalArticles: 'articles',
      resultsFor: 'results for',
      clearSearch: 'Clear search',
      noResults: 'No results',
      noResultsDesc: 'No articles found matching your search.',
      quickLinks: 'Quick Links',
      popularTopics: 'Popular Topics',
      needHelp: 'Need help?',
      needHelpDesc: "Can't find what you're looking for? Contact our support team.",
      contactSupport: 'Contact Support',
      githubRepo: 'GitHub Repository',
      apiPlayground: 'API Playground',
      changelog: 'Changelog',
      statusPage: 'System Status',
      releaseNotes: 'Release Notes',
      // Categories
      gettingStarted: 'Getting Started',
      gettingStartedDesc: 'Quick start guides, installation and initial platform configuration.',
      agentDeployment: 'Agent Deployment',
      agentDeploymentDesc: 'Install and manage agents on Windows, Linux and via group policies.',
      securityModules: 'Security Modules',
      securityModulesDesc: 'Email Security, IOC management, SIEM, vulnerability scanner, AD attacks and compliance.',
      apiReference: 'API Reference',
      apiReferenceDesc: 'REST API documentation, authentication, webhooks and rate limits.',
      administration: 'Administration',
      administrationDesc: 'User management, RBAC, SSO configuration and multi-tenancy.',
      troubleshooting: 'Troubleshooting',
      troubleshootingDesc: 'Common issues, agent connectivity, scan failures and log analysis.',
      // Articles - Getting Started
      quickStartGuide: 'Quick Start Guide',
      quickStartGuideDesc: 'Get the platform up and running in less than 15 minutes with this step-by-step guide.',
      installationGuide: 'Installation Guide',
      installationGuideDesc: 'System requirements, Docker installation and environment setup.',
      firstScan: 'Your First Scan',
      firstScanDesc: 'Run your first vulnerability scan and understand the results.',
      architectureOverview: 'Architecture Overview',
      architectureOverviewDesc: 'System components, data flows and security design.',
      // Articles - Agent Deployment
      windowsAgent: 'Windows Agent',
      windowsAgentDesc: 'Agent installation and configuration for Windows Server and Desktop environments.',
      linuxAgent: 'Linux Agent',
      linuxAgentDesc: 'Agent deployment on Debian, Ubuntu, RHEL and CentOS distributions.',
      groupPolicies: 'GPO Deployment',
      groupPoliciesDesc: 'Mass agent deployment via Group Policy Objects in Active Directory.',
      autoUpdate: 'Auto-Update',
      autoUpdateDesc: 'Configure the agent auto-update mechanism.',
      agentTroubleshooting: 'Agent Diagnostics',
      agentTroubleshootingDesc: 'Troubleshoot common agent connection and performance issues.',
      // Articles - Security Modules
      emailSecurityDoc: 'Email Security',
      emailSecurityDocDesc: 'SPF, DKIM, DMARC analysis and email threat detection.',
      iocManagement: 'IOC Management',
      iocManagementDesc: 'Import, search and manage indicators of compromise.',
      miniSiemDoc: 'Mini SIEM',
      miniSiemDocDesc: 'Real-time security event collection and analysis.',
      vulnScanner: 'Vulnerability Scanner',
      vulnScannerDesc: 'CVE vulnerability scanning and per-asset risk assessment.',
      adAttacksDoc: 'Active Directory Attacks',
      adAttacksDocDesc: 'BloodHound-style attack path detection in AD environments.',
      complianceModule: 'Compliance Module',
      complianceModuleDesc: 'GDPR, ISO 27001, NIST, PCI-DSS and ENS compliance assessment.',
      // Articles - API Reference
      restApiDocs: 'REST API Documentation',
      restApiDocsDesc: 'Complete endpoint reference with parameters and response schemas.',
      authenticationApi: 'Authentication API',
      authenticationApiDesc: 'JWT tokens, API keys and authentication flows for integrations.',
      webhooks: 'Webhooks',
      webhooksDesc: 'Configure webhooks to receive real-time notifications.',
      rateLimits: 'Rate Limits',
      rateLimitsDesc: 'Rate limiting, quotas and best practices for API consumption.',
      // Articles - Administration
      userManagement: 'User Management',
      userManagementDesc: 'Create, edit and manage user accounts and permissions.',
      rbac: 'Access Control (RBAC)',
      rbacDesc: 'Configure roles, permissions and role-based access policies.',
      ssoConfig: 'SSO Configuration',
      ssoConfigDesc: 'Integration with SAML, OAuth2 and corporate identity providers.',
      multiTenancy: 'Multi-Tenancy',
      multiTenancyDesc: 'Configure and manage multiple tenants and data isolation.',
      // Articles - Troubleshooting
      commonIssues: 'Common Issues',
      commonIssuesDesc: 'Solutions to the most frequently reported user issues.',
      agentConnectivity: 'Agent Connectivity',
      agentConnectivityDesc: 'Diagnose network, firewall and backend connectivity problems.',
      scanFailures: 'Scan Failures',
      scanFailuresDesc: 'Resolve failed scans, timeouts and permission errors.',
      logAnalysis: 'Log Analysis',
      logAnalysisDesc: 'Log locations, interpretation and usage for advanced diagnostics.',
    },

    // AD Attacks
    adAttacks: {
      scanFailedTag: 'AD scan failed',
      scanFailedTitle: 'The AD scan ran but failed.',
      scanFailedDetails: 'Errors by section:',
      scanFailedHint: 'Check the credentials, connectivity to the Domain Controller and that the agent is domain-joined, then run the scan again.',
    },

    // Errors
    errors: {
      generic: 'An error occurred',
      network: 'Connection error',
      unauthorized: 'Unauthorized',
      forbidden: 'Access denied',
      notFound: 'Not found',
      serverError: 'Server error',
      validation: 'Validation error',
      sessionExpired: 'Session expired'
    },

    // Success messages
    success: {
      saved: 'Saved successfully',
      deleted: 'Deleted successfully',
      updated: 'Updated successfully',
      created: 'Created successfully',
      imported: 'Imported successfully',
      exported: 'Exported successfully'
    },

    // Per-block "not available" notice: the backend lists in `_degraded` which
    // part of the response it could not compute. See lib/degraded.js.
    degraded: {
      notice: 'Data not available right now: {blocks}. It could not be read — this is not a real zero.',
      chip: 'not available',
      blocks: {
        response: 'this data',
        today_score: "today's score",
        mttd: 'mean time to detect (MTTD)',
        mttr: 'mean time to resolve (MTTR)',
        compliance: 'compliance',
        exposure: 'exposure',
        incidents: 'incidents',
        attack_surface: 'attack surface',
        dlp: 'data loss (DLP)',
        patch_lag: 'patch lag',
        agents: 'agents',
        scans: 'scans',
        vulnerabilities: 'vulnerabilities',
        ad_scans: 'Active Directory scans',
        network_scans: 'network scans',
        attack_paths: 'attack paths',
        database: 'database',
        alerts: 'alerts',
        ingestion: 'event ingestion',
        trends: 'dashboard trends',
        executive: 'executive summary',
        packs: 'attack packs',
        activations: 'pack activations',
        correlation_rules: 'correlation rules',
        correlation_alerts: 'correlation alerts',
        correlation_stats: 'correlation stats',
        bas_dashboard: 'BAS summary',
        bas_trend: 'BAS score trend',
        bas_campaigns: 'BAS campaigns',
        bas_vendors: 'vendor catalog',
        bas_siem_vendors: 'saved SIEM/EDR stack',
        attack_library_tests: 'attack library tests',
        siem_config: 'SIEM configuration (showing default values)',
        agent_version: 'latest agent version',
        runbook: 'the suggested runbook for this alert'
      }
    }
  }
};

/**
 * Get translation for a key
 * @param {string} key - Dot-separated key (e.g., 'common.save')
 * @param {string} lang - Language code
 * @returns {string} Translated string
 */
export function t(key, lang = DEFAULT_LANGUAGE) {
  const keys = key.split('.');
  let value = translations[lang];

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // Fallback to default language
      value = translations[DEFAULT_LANGUAGE];
      for (const k2 of keys) {
        if (value && typeof value === 'object' && k2 in value) {
          value = value[k2];
        } else {
          return key; // Return key if not found
        }
      }
      break;
    }
  }

  return typeof value === 'string' ? value : key;
}

/**
 * Get all translations for a language
 * @param {string} lang - Language code
 * @returns {object} Translations object
 */
export function getTranslations(lang = DEFAULT_LANGUAGE) {
  return translations[lang] || translations[DEFAULT_LANGUAGE];
}

export default translations;
